import { DOCUMENT } from '@angular/common';
import { Injectable, Inject, RendererFactory2, Renderer2 } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import {
  PlatformInformationService,
  PlatformassetPipe
} from '../../../../projects/platform-manager/src/public_api';
import { SpecialSalesService } from './special-sales.service';

@Injectable({ providedIn: 'root' })
export class AppColorService {
  private renderer: Renderer2;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    rendererFactory: RendererFactory2,
    private assetPipe: PlatformassetPipe,
    private platformService: PlatformInformationService,
    private specialSaleService: SpecialSalesService
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  /**
   * returns an object containing all currently used CSS Vars (key/value)
   * @param type the platform or special type you want to use (platformshortcode lowercase, partner, darktheme)
   */
  async getColorFor(type: string | { [key: string]: string }): Promise<{ [key: string]: string }> {
    const variables = {
      // <- init general, overwritten by switch below
      // initial color values - from alpi. Might change all to #fff and use the fallback mechanic in the satellite service
      // TEXT COLORS
      '--primary-text-color': '#706f6f',
      '--secondary-text-color': '#f7f7f7',
      '--third-text-color': '#000',
      '--fourth-text-color': '#CA4D00',
      '--fifteenth-text-color': '#a31f16',
      '--nineteenth-text-color': '#f1d6d3',
      '--danger-text-color': '#ff2b2b',
      '--grayed-out-text-color': '#fff',

      // BORDER COLORS
      '--primary-border-color': '#706f6f',
      '--secondary-border-color': '#f7f7f7',
      '--third-border-color': '#CA4D00',
      '--fourth-border-color': '#09505d',
      '--fifth-border-color': '#f1d6d3',
      '--tenth-border-color': '#e5e5e5',
      '--nineteenth-border-color': '#a31f16',
      '--danger-border-color': '#fff',

      // SHADOW COLORS
      '--primary-shadow-color': '#000',
      '--secondary-shadow-color': '#f7f7f7',
      '--third-shadow-color': '#01010180',
      '--fourth-shadow-color': '#09505d',

      // BACKGROUND COLORS
      '--primary-color': '#CA4D00',
      '--primary-color-with-opacity90': '#CA4D00e6',
      '--secondary-color': '#CA4D00',
      '--third-color': '#CA4D00',
      '--fourth-color': '#ffffff',
      '--fifth-color': '#e6edee',
      '--sixth-color': '#e5e5e5',
      '--seventh-color': '#f7f7f7',
      '--eighth-color': '#CA4D00',
      '--ninth-color': '#22b835',
      '--tenth-color': '#f1d6d3',
      '--twelfth-color': '#706f6f',
      '--fortyseventh-color': '#dfc342',
      '--fortyeigth-color': '#000',
      '--danger-color': '#ff2b2b',
      '--grayed-out-color': '#b1b1b1',
      '--primary-slate-color': '#CA4D00',
      '--secondary-slate-color': '#CA4D00',

      // FONT VARIABLES:
      '--font-primary': 'Lato',
      '--font-secondary': 'Brandon Grotesque',
      '--font-bold': 'BrandonGrotesqueBlack'
    };

    if (typeof type !== 'string') {
      // Microsites and iFrame. Make sure to not double add '--'
      Object.keys(type).forEach(style_variable => {
        variables['--' + style_variable.replace('--', '')] = type[style_variable];
      });
    } else {
      // Satellites
      switch (type) {
        case 'sb': {
          // TEXT COLORS
          variables['--third-text-color'] = '#428bca';
          variables['--fourth-text-color'] = '#e91230';
          variables['--fifteenth-text-color'] = '#252525';
          variables['--nineteenth-text-color'] = '#cecece';

          // BORDER COLORS
          variables['--third-border-color'] = '#ff2140';
          variables['--fourth-border-color'] = '#428bca';
          variables['--fifth-border-color'] = '#252525';
          variables['--nineteenth-border-color'] = '#252525';

          // BACKGROUND COLORS
          variables['--primary-color'] = '#ff2140';
          variables['--primary-color-with-opacity90'] = '#ff2140e6';
          variables['--secondary-color'] = '#428bca';
          variables['--third-color'] = '#252525';
          variables['--eighth-color'] = '#252525';
          variables['--tenth-color'] = '#f6dbdf';
          variables['--primary-slate-color'] = '#ff6464';
          variables['--secondary-slate-color'] = '#ff2140';
          break;
        }
        case 'df': {
          // TEXT COLORS
          variables['--third-text-color'] = '#4A576A';
          variables['--fourth-text-color'] = '#c53b7b';
          variables['--fifteenth-text-color'] = '#509b1e';
          variables['--nineteenth-text-color'] = '#bed4ae';

          // BORDER COLORS
          variables['--third-border-color'] = '#E4448F';
          variables['--fourth-border-color'] = '#4A576A';
          variables['--fifth-border-color'] = '#509b1e';
          variables['--nineteenth-border-color'] = '#509b1e';

          // BACKGROUND COLORS
          variables['--primary-color'] = '#E4448F';
          variables['--primary-color-with-opacity90'] = '#E4448FE6';
          variables['--secondary-color'] = '#4A576A';
          variables['--third-color'] = '#509b1e';
          variables['--eighth-color'] = '#509b1e';
          variables['--tenth-color'] = '#eed1d1';
          variables['--primary-slate-color'] = '#747679';
          variables['--secondary-slate-color'] = '#4A576A';
          break;
        }
        case 'du': {
          // TEXT COLORS
          variables['--third-text-color'] = '#4A576A';
          variables['--fourth-text-color'] = '#c53b7b';
          variables['--fifteenth-text-color'] = '#509b1e';
          variables['--nineteenth-text-color'] = '#bed4ae';

          // BORDER COLORS
          variables['--third-border-color'] = '#E4448F';
          variables['--fourth-border-color'] = '#4A576A';
          variables['--fifth-border-color'] = '#509b1e';
          variables['--nineteenth-border-color'] = '#509b1e';

          // BACKGROUND COLORS
          variables['--primary-color'] = '#E4448F';
          variables['--primary-color-with-opacity90'] = '#E4448FE6';
          variables['--secondary-color'] = '#4A576A';
          variables['--third-color'] = '#509b1e';
          variables['--eighth-color'] = '#509b1e';
          variables['--tenth-color'] = '#eed1d1';
          variables['--primary-slate-color'] = '#747679';
          variables['--secondary-slate-color'] = '#4A576A';
          break;
        }
        case 'mc': {
          // TEXT COLORS
          variables['--third-text-color'] = '#E9612F';
          variables['--fourth-text-color'] = '#428bca';
          variables['--fifteenth-text-color'] = '#509b1e';
          variables['--nineteenth-text-color'] = '#bed4ae';

          // BORDER COLORS
          variables['--third-border-color'] = '#E4448F';
          variables['--fourth-border-color'] = '#4A576A';
          variables['--fifth-border-color'] = '#428bca';
          variables['--nineteenth-border-color'] = '#d2e1ef';

          // BACKGROUND COLORS
          variables['--primary-color'] = '#72d6fa';
          variables['--primary-color-with-opacity90'] = '#72d6fae6';
          variables['--secondary-color'] = '#E9612F';
          variables['--third-color'] = '#428bca';
          variables['--eighth-color'] = '#428bca';
          variables['--tenth-color'] = '#d2e1ef';
          variables['--primary-slate-color'] = '#72d6fa';
          variables['--secondary-slate-color'] = '#428bca';
          break;
        }
        case 'bp': {
          // TEXT COLORS
          variables['--third-text-color'] = '#E9612F';
          variables['--fourth-text-color'] = '#428bca';
          variables['--fifteenth-text-color'] = '#509b1e';
          variables['--nineteenth-text-color'] = '#bed4ae';

          // BORDER COLORS
          variables['--third-border-color'] = '#E4448F';
          variables['--fourth-border-color'] = '#4A576A';
          variables['--fifth-border-color'] = '#428bca';
          variables['--nineteenth-border-color'] = '#d2e1ef';

          // BACKGROUND COLORS
          variables['--primary-color'] = '#72d6fa';
          variables['--primary-color-with-opacity90'] = '#72d6fae6';
          variables['--secondary-color'] = '#E9612F';
          variables['--third-color'] = '#428bca';
          variables['--eighth-color'] = '#428bca';
          variables['--tenth-color'] = '#d2e1ef';
          variables['--primary-slate-color'] = '#72d6fa';
          variables['--secondary-slate-color'] = '#428bca';
          break;
        }
        case 'pf': {
          // TEXT COLORS
          variables['--third-text-color'] = '#da1c23';
          variables['--fourth-text-color'] = '#0075be';
          variables['--fifteenth-text-color'] = '#0075be';
          variables['--nineteenth-text-color'] = '#f0cdcf';

          // BORDER COLORS
          variables['--third-border-color'] = '#0075be';
          variables['--fourth-border-color'] = '#da1c23';
          variables['--fifth-border-color'] = '#b3ebf0';
          variables['--nineteenth-border-color'] = '#da1c23';

          // BACKGROUND COLORS
          variables['--primary-color'] = '#0075be';
          variables['--primary-color-with-opacity90'] = '#0075bee6';
          variables['--secondary-color'] = '#da1c23';
          variables['--third-color'] = '#da1c23';
          variables['--eighth-color'] = '#da1c23';
          variables['--tenth-color'] = '#cddcf0';
          variables['--primary-slate-color'] = '#c5060d';
          variables['--secondary-slate-color'] = '#da1c23';
          break;
        }
        case 'sf': {
          // TEXT COLORS
          variables['--third-text-color'] = '#da1c23';
          variables['--fourth-text-color'] = '#0075be';
          variables['--fifteenth-text-color'] = '#0075be';
          variables['--nineteenth-text-color'] = '#f0cdcf';

          // BORDER COLORS
          variables['--third-border-color'] = '#0075be';
          variables['--fourth-border-color'] = '#da1c23';
          variables['--fifth-border-color'] = '#b3ebf0';
          variables['--nineteenth-border-color'] = '#da1c23';

          // BACKGROUND COLORS
          variables['--primary-color'] = '#0075be';
          variables['--primary-color-with-opacity90'] = '#0075bee6';
          variables['--secondary-color'] = '#da1c23';
          variables['--third-color'] = '#da1c23';
          variables['--eighth-color'] = '#da1c23';
          variables['--tenth-color'] = '#cddcf0';
          variables['--primary-slate-color'] = '#c5060d';
          variables['--secondary-slate-color'] = '#da1c23';
          break;
        }
        case 'sm': {
          // TEXT COLORS
          variables['--third-text-color'] = '#ce110b';
          variables['--fourth-text-color'] = '#0051a2';
          variables['--fifteenth-text-color'] = '#0051a2';
          variables['--nineteenth-text-color'] = '#eec3c1';

          // BORDER COLORS
          variables['--third-border-color'] = '#0051a2';
          variables['--fourth-border-color'] = '#ce110b';
          variables['--fifth-border-color'] = '#d6e3f0';
          variables['--nineteenth-border-color'] = '#ce110b';

          // BACKGROUND COLORS
          variables['--primary-color'] = '#0051a2';
          variables['--primary-color-with-opacity90'] = '#0051a2e6';
          variables['--secondary-color'] = '#ce110b';
          variables['--third-color'] = '#ce110b';
          variables['--eighth-color'] = '#ce110b';
          variables['--tenth-color'] = '#d6e3f0';
          variables['--primary-slate-color'] = '#0051a2';
          variables['--secondary-slate-color'] = '#001f3f';
          break;
        }
        case 's2': {
          variables['--primary-text-color'] = '#666';
          variables['--secondary-text-color'] = '#fff';
          variables['--third-text-color'] = '#545454';
          variables['--fourth-text-color'] = '#545454';
          variables['--fifteenth-text-color'] = '#1c1c18';
          variables['--nineteenth-text-color'] = '#d3d3d3';
          variables['--third-border-color'] = '#008aff';
          variables['--fourth-border-color'] = '#1c1c18';
          variables['--fifth-border-color'] = '#d3d3d3';
          variables['--nineteenth-border-color'] = '#252525';
          variables['--primary-color'] = '#008aff';
          variables['--primary-color-with-opacity90'] = '#008affe6';
          variables['--secondary-color'] = '#35b021';
          variables['--third-color'] = '#014475';
          variables['--eighth-color'] = '#014475';
          variables['--seventh-color'] = '#ffffff';
          variables['--tenth-color'] = '#e5e5e5';
          variables['--twelfth-color'] = '#014475';
          variables['--primary-slate-color'] = '#6bc2ff';
          variables['--secondary-slate-color'] = '#45aff9';
          break;
        }
        case 'es': {
          variables['--primary-text-color'] = '#333';
          variables['--third-text-color'] = '#333';
          variables['--fourth-text-color'] = '#333';
          variables['--fifteenth-text-color'] = '#333';
          variables['--nineteenth-text-color'] = '#d3d3d3';
          variables['--third-border-color'] = '#1c1c18';
          variables['--fourth-border-color'] = '#1c1c18';
          variables['--fifth-border-color'] = '#d3d3d3';
          variables['--nineteenth-border-color'] = '#252525';
          variables['--primary-color'] = '#01adbb';
          variables['--primary-color-with-opacity90'] = '#01adbb';
          variables['--secondary-color'] = '#f07d00';
          variables['--third-color'] = '#333';
          variables['--fifth-color'] = '#fcf4ec';
          variables['--eighth-color'] = '#fff'; // desktop header
          variables['--tenth-color'] = '#e5e5e5';
          variables['--twelfth-color'] = '#171717';
          variables['--primary-slate-color'] = '#01adbb';
          variables['--secondary-slate-color'] = '#098a95';
          break;
        }
        case 'ar': {
          if (!this.platformService.getCurrentPartner()) {
            variables['--fortyninth-text-color'] = '#000'; // Black for Alpy Blue
            variables['--fortyninth-color'] = '#71E2EB'; // Alpy Blue
            variables['--secondary-text-color'] = '#fff';
            variables['--font-primary'] = 'foco';
            variables['--font-secondary'] = 'focoBold';
            variables['--font-bold'] = 'focoBlack';
          }
        }
      }

      const sale = this.specialSaleService.getActiveSale();
      if (sale?.colors) {
        Object.keys(sale.colors).forEach(key => {
          variables[key] = sale.colors[key];
        });
      }
    }

    // we have a partner on our hands. Set up the colors for it immediately to prevent flicker.
    if (this.platformService.getCurrentPartner()) {
      // this.platformService.updatePartner(this.platformService.getCurrentPartner());
      const config = await lastValueFrom(this.platformService.microsite$);
      Object.keys(config.config.style_variables).forEach(v => {
        variables['--' + v] = config.config.style_variables[v];
      });
      variables['--font-primary'] = 'Lato';
      variables['--font-secondary'] = 'Brandon Grotesque';
      variables['--font-bold'] = 'BrandonGrotesqueBlack';
    }

    return variables;
  }

  /**
   * injects a <style> into the header containing all needed @font-face for the platform
   * can be run multiple times in SSR and client
   * @param platform the platformshortcode to select the right fonts
   */
  injectFonts(platform: string) {
    if (!this.document.getElementById('injected-fonts')) {
      // if (platform === 'ar') {
      //   const style = this.renderer.createElement('link');
      //   style.rel = 'stylesheet';
      //   style.href = 'https://use.typekit.net/lcp7zrs.css';
      //   style.id = 'injected-fonts';
      //   this.renderer.insertBefore(this.document.head, style, this.document.head.firstChild);
      // } else {
      const style = this.renderer.createElement('style');
      style.id = 'injected-fonts';
      let fonts: Array<{
        fontFamily: string;
        url: string;
        fontWeight: number | string;
        fontStyle: string;
        fontDisplay: string;
        format: string;
        unicodeRange?: string;
      }>;
      switch (platform) {
        case 'es':
          fonts = [
            // Lato 400 (normal)
            {
              fontFamily: 'Lato',
              url: 'webfonts/opensans-ext.woff',
              fontWeight: 400,
              fontStyle: 'normal',
              fontDisplay: 'swap',
              format: 'woff',
              unicodeRange:
                'U+0100-02AF, U+0300-0301, U+0303-0304, U+0308-0309, U+0323, U+0329, U+1E00-1EFF, U+2020, U+20A0-20AB, U+20AD-20CF, U+2113, U+2C60-2C7F, U+A720-A7FF'
            },
            {
              fontFamily: 'Lato',
              url: 'webfonts/opensans.woff',
              fontWeight: 400,
              fontStyle: 'normal',
              fontDisplay: 'swap',
              format: 'woff',
              unicodeRange:
                'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0300-0301, U+0303-0304, U+0308-0309, U+0323, U+0329, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD'
            },
            // Brandon Grotesque 400
            {
              fontFamily: 'Brandon Grotesque',
              url: 'webfonts/poppins-reg-ext.woff2',
              fontWeight: 400,
              fontStyle: 'normal',
              fontDisplay: 'swap',
              format: 'woff2',
              unicodeRange:
                'U+0100-02AF, U+0300-0301, U+0303-0304, U+0308-0309, U+0323, U+0329, U+1E00-1EFF, U+2020, U+20A0-20AB, U+20AD-20CF, U+2113, U+2C60-2C7F, U+A720-A7FF'
            },
            {
              fontFamily: 'Brandon Grotesque',
              url: 'webfonts/poppins-reg.woff2',
              fontWeight: 400,
              fontStyle: 'normal',
              fontDisplay: 'swap',
              format: 'woff2',
              unicodeRange:
                'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0300-0301, U+0303-0304, U+0308-0309, U+0323, U+0329, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD'
            },
            // Brandon Grotesque Black 700 400
            {
              fontFamily: 'BrandonGrotesqueBlack',
              url: 'webfonts/poppins-ext.woff2',
              fontWeight: '700 400',
              fontStyle: 'normal',
              fontDisplay: 'fallback',
              format: 'woff2',
              unicodeRange:
                'U+0100-02AF, U+0300-0301, U+0303-0304, U+0308-0309, U+0323, U+0329, U+1E00-1EFF, U+2020, U+20A0-20AB, U+20AD-20CF, U+2113, U+2C60-2C7F, U+A720-A7FF'
            },
            {
              fontFamily: 'BrandonGrotesqueBlack',
              url: 'webfonts/poppins.woff2',
              fontWeight: '700 400',
              fontStyle: 'normal',
              fontDisplay: 'fallback',
              format: 'woff2',
              unicodeRange:
                'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0300-0301, U+0303-0304, U+0308-0309, U+0323, U+0329, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD'
            }
          ];
          break;
        case 'ar':
          fonts = [
            {
              fontFamily: 'foco',
              url: 'webfonts/foco/foco_regular.woff2',
              fontWeight: 400,
              fontStyle: 'normal',
              fontDisplay: 'swap',
              format: 'woff2'
            },
            {
              fontFamily: 'focoBold',
              url: 'webfonts/foco/foco_bold.woff2',
              fontWeight: 700,
              fontStyle: 'normal',
              fontDisplay: 'swap',
              format: 'woff2'
            },
            {
              fontFamily: 'focoBlack',
              url: 'webfonts/foco/foco_bold.woff2',
              fontWeight: '700',
              fontStyle: 'normal',
              fontDisplay: 'swap',
              format: 'woff2'
            }
          ];
          break;
        default:
          fonts = [
            // Lato 400 (normal)
            {
              fontFamily: 'Lato',
              url: 'webfonts/lato400range1.woff2',
              fontWeight: 400,
              fontStyle: 'normal',
              fontDisplay: 'swap',
              format: 'woff2',
              unicodeRange:
                'U+0100-024F, U+0259, U+1E00-1EFF, U+2020, U+20A0-20AB, U+20AD-20CF, U+2113, U+2C60-2C7F, U+A720-A7FF'
            },
            {
              fontFamily: 'Lato',
              url: 'webfonts/lato400range2.woff2',
              fontWeight: 400,
              fontStyle: 'normal',
              fontDisplay: 'swap',
              format: 'woff2',
              unicodeRange:
                'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD'
            },
            // Lato 700 (bold)
            {
              fontFamily: 'Lato',
              url: 'webfonts/lato700range1.woff2',
              fontWeight: 700,
              fontStyle: 'normal',
              fontDisplay: 'swap',
              format: 'woff2',
              unicodeRange:
                'U+0100-024F, U+0259, U+1E00-1EFF, U+2020, U+20A0-20AB, U+20AD-20CF, U+2113, U+2C60-2C7F, U+A720-A7FF'
            },
            {
              fontFamily: 'Lato',
              url: 'webfonts/lato700range2.woff2',
              fontWeight: 700,
              fontStyle: 'normal',
              fontDisplay: 'swap',
              format: 'woff2',
              unicodeRange:
                'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD'
            },
            // Brandon 400 and 700 (normal, bold)
            {
              fontFamily: 'Brandon Grotesque',
              url: 'webfonts/brandongrotesque/BrandonGrotesque-Regular.woff2',
              fontWeight: 400,
              fontStyle: 'normal',
              fontDisplay: 'swap',
              format: 'woff2'
            },
            {
              fontFamily: 'BrandonGrotesqueBlack',
              url: 'webfonts/brandongrotesque/BrandonGrotesque-Black.woff2',
              fontWeight: '700 400',
              fontStyle: 'normal',
              fontDisplay: 'swap',
              format: 'woff2'
            }
          ];
          break;
      }
      // setup font <style> and add to header
      this.setFontStyleContent(fonts, style);
      this.renderer.insertBefore(this.document.head, style, this.document.head.firstChild);
      // add preload links as first child "above"
      fonts.forEach(font => {
        const preLink = this.renderer.createElement('link');
        preLink.setAttribute('href', this.assetPipe.transform(font.url, true));
        preLink.setAttribute('type', 'font/' + font.format);
        preLink.setAttribute('as', 'font');
        preLink.setAttribute('rel', 'preload');
        preLink.setAttribute('name', 'fontPrelink');
        preLink.setAttribute('crossorigin', '');
        this.renderer.insertBefore(this.document.head, preLink, this.document.head.firstChild);
      });
      // }
      if (this.platformService.getCurrentPartner()) {
        this.injectPartnerFonts(this.platformService.getCurrentPartner());
      }
    }
  }

  /**
   * overwrites font <style> declaration of platform and injects partner specific fonts
   *
   * @param partner
   */
  injectPartnerFonts(partner: string) {
    let fontEl = this.document.getElementById('injected-fonts');
    let hadEl = true;
    if (!fontEl) {
      fontEl = this.renderer.createElement('style');
      hadEl = false;
    } else {
      // had element and probably had preloads, remove them (SSR)
      const preLoads = this.document.getElementsByName('fontPrelink');
      Array.prototype.slice.call(preLoads).forEach(link => {
        (link as HTMLLinkElement).remove();
      });
    }
    if (!fontEl.getAttribute('data-partner')) {
      this.renderer.setAttribute(fontEl, 'data-partner', partner);
      let fonts: Array<{
        fontFamily: string;
        url: string;
        fontWeight: number | string;
        fontStyle: string;
        fontDisplay: string;
        format: string;
        unicodeRange?: string;
      }>;
      switch (partner) {
        default:
          fonts = [
            // Lato 400 (normal)
            {
              fontFamily: 'Lato',
              url: 'webfonts/opensans-ext.woff',
              fontWeight: 400,
              fontStyle: 'normal',
              fontDisplay: 'swap',
              format: 'woff',
              unicodeRange:
                'U+0100-02AF, U+0300-0301, U+0303-0304, U+0308-0309, U+0323, U+0329, U+1E00-1EFF, U+2020, U+20A0-20AB, U+20AD-20CF, U+2113, U+2C60-2C7F, U+A720-A7FF'
            },
            {
              fontFamily: 'Lato',
              url: 'webfonts/opensans.woff',
              fontWeight: 400,
              fontStyle: 'normal',
              fontDisplay: 'swap',
              format: 'woff',
              unicodeRange:
                'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0300-0301, U+0303-0304, U+0308-0309, U+0323, U+0329, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD'
            },
            // Lato 700 (bold)
            {
              fontFamily: 'Lato',
              url: 'webfonts/opensans700-ext.woff2',
              fontWeight: 700,
              fontStyle: 'normal',
              fontDisplay: 'swap',
              format: 'woff2',
              unicodeRange:
                'U+0100-02AF, U+0300-0301, U+0303-0304, U+0308-0309, U+0323, U+0329, U+1E00-1EFF, U+2020, U+20A0-20AB, U+20AD-20CF, U+2113, U+2C60-2C7F, U+A720-A7FF'
            },
            {
              fontFamily: 'Lato',
              url: 'webfonts/opensans700.woff2',
              fontWeight: 700,
              fontStyle: 'normal',
              fontDisplay: 'swap',
              format: 'woff2',
              unicodeRange:
                'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0300-0301, U+0303-0304, U+0308-0309, U+0323, U+0329, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD'
            },
            // Lato 400 (normal)
            {
              fontFamily: 'Brandon Grotesque',
              url: 'webfonts/opensans-ext.woff',
              fontWeight: 400,
              fontStyle: 'normal',
              fontDisplay: 'swap',
              format: 'woff',
              unicodeRange:
                'U+0100-02AF, U+0300-0301, U+0303-0304, U+0308-0309, U+0323, U+0329, U+1E00-1EFF, U+2020, U+20A0-20AB, U+20AD-20CF, U+2113, U+2C60-2C7F, U+A720-A7FF'
            },
            {
              fontFamily: 'Brandon Grotesque',
              url: 'webfonts/opensans.woff',
              fontWeight: 400,
              fontStyle: 'normal',
              fontDisplay: 'swap',
              format: 'woff',
              unicodeRange:
                'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0300-0301, U+0303-0304, U+0308-0309, U+0323, U+0329, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD'
            },
            // Lato 700 (bold)
            {
              fontFamily: 'BrandonGrotesqueBlack',
              url: 'webfonts/opensans700-ext.woff2',
              fontWeight: 700,
              fontStyle: 'normal',
              fontDisplay: 'swap',
              format: 'woff2',
              unicodeRange:
                'U+0100-02AF, U+0300-0301, U+0303-0304, U+0308-0309, U+0323, U+0329, U+1E00-1EFF, U+2020, U+20A0-20AB, U+20AD-20CF, U+2113, U+2C60-2C7F, U+A720-A7FF'
            },
            {
              fontFamily: 'BrandonGrotesqueBlack',
              url: 'webfonts/opensans700.woff2',
              fontWeight: 700,
              fontStyle: 'normal',
              fontDisplay: 'swap',
              format: 'woff2',
              unicodeRange:
                'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0300-0301, U+0303-0304, U+0308-0309, U+0323, U+0329, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD'
            }
          ];
          break;
      }
      this.setFontStyleContent(fonts, fontEl);
      if (!hadEl) {
        this.renderer.insertBefore(this.document.head, fontEl, this.document.head.firstChild);
      }

      fonts.forEach(font => {
        const preLink = this.renderer.createElement('link');
        preLink.setAttribute('href', this.assetPipe.transform(font.url, true));
        preLink.setAttribute('type', 'font/' + font.format);
        preLink.setAttribute('as', 'font');
        preLink.setAttribute('rel', 'preload');
        preLink.setAttribute('crossorigin', '');
        this.renderer.insertBefore(this.document.head, preLink, this.document.head.firstChild);
      });
    }
  }

  private setFontStyleContent(
    fonts: Array<{
      fontFamily: string;
      url: string;
      fontWeight: number | string;
      fontStyle: string;
      fontDisplay: string;
      format: string;
      unicodeRange?: string;
    }>,
    element: HTMLElement
  ) {
    element.innerHTML = '';
    fonts.forEach(font => {
      element.innerHTML += this.createFontFace(
        font.fontFamily,
        font.url,
        font.fontWeight,
        font.fontStyle,
        font.fontDisplay as 'swap',
        font.format,
        font.unicodeRange
      );
    });
  }

  /**
   * returns a css @font-face for use as injectable string for a <style>.innerHTML
   * @param fontFamily name of the font-family. Has to match with used names in fonts.scss (Lato | Brandon Grotesque |BrandonGrotesqueBlack)
   * @param url the *relative* URL from a self hosted font. Don't forget to check the webfonts asset folder!
   * @param fontWeight the font-weight as 400,700 or normal, bold
   * @param fontStyle the font-style. Defaults to normal.
   * @param fontDisplay the font-display. Defaults to swap.
   * @param format the font format. Defaults to woff2.
   * @param unicodeRange the optional unicodeRange to use.
   * @returns the complete string of a @font-face{} to inject as innerHTML
   */
  private createFontFace(
    fontFamily: string,
    url: string,
    fontWeight: string | number = 400,
    fontStyle = 'normal',
    fontDisplay: 'auto' | 'block' | 'swap' | 'fallback' | 'optional' = 'swap',
    format = 'woff2',
    unicodeRange?: string
  ): string {
    return `
    @font-face {
      font-family: "${fontFamily}";
      font-style: ${fontStyle};
      font-weight: ${fontWeight};
      font-display: ${fontDisplay};
      src: url('${this.assetPipe.transform(url, true)}') format('${format}');
      ${unicodeRange ? `unicode-range: ${unicodeRange}` : ''};
    }
    `;
  }
}
